import { type ReactNode } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/utils/cn';

type Props = {
  page: number;
  totalPages: number;
  totalItems: number;
  onPageChange: (p: number) => void;
};

const BTN = cn(
  'flex h-7 w-7 items-center justify-center rounded-md transition-colors',
  'text-text-tertiary hover:bg-white/[0.06] hover:text-text-primary',
  'disabled:opacity-30 disabled:pointer-events-none'
);

function NavButton({
  disabled,
  onClick,
  children,
}: {
  disabled: boolean;
  onClick: () => void;
  children: ReactNode;
}): ReactNode {
  return (
    <button onClick={onClick} disabled={disabled} className={BTN}>
      {children}
    </button>
  );
}

function PageNav({
  page,
  totalPages,
  onPageChange,
}: Omit<Props, 'totalItems'>): ReactNode {
  return (
    <div className="flex items-center gap-xs">
      <span className="font-mono text-[11px] tabular-nums text-text-tertiary/60">
        {page}/{totalPages}
      </span>
      <NavButton disabled={page <= 1} onClick={() => onPageChange(page - 1)}>
        <ChevronLeft size={14} />
      </NavButton>
      <NavButton
        disabled={page >= totalPages}
        onClick={() => onPageChange(page + 1)}
      >
        <ChevronRight size={14} />
      </NavButton>
    </div>
  );
}

export function TableFooter({
  page,
  totalPages,
  totalItems,
  onPageChange,
}: Props): ReactNode {
  return (
    <div className="flex items-center justify-between border-t border-white/[0.06] px-md py-sm">
      <span className="font-mono text-[11px] tabular-nums text-text-tertiary/60">
        {totalItems} creator{totalItems !== 1 ? 's' : ''}
      </span>
      <PageNav
        page={page}
        totalPages={totalPages}
        onPageChange={onPageChange}
      />
    </div>
  );
}
